import { captureException } from "@sentry/nextjs";
import { Component } from "react";

type Props = { children: React.ReactNode; context: string; layout?: string };

type State = { hasError: boolean };

class GracefulErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: any, errorInfo: React.ErrorInfo) {
    captureException(error, {
      tags: {
        context: this.props.context,
        layout: this.props.layout || null,
        captureMethod: "ErrorBoundary",
      },
      extra: {
        componentStack: errorInfo.componentStack,
      },
    });
  }

  render() {
    if (this.state.hasError) {
      return null;
    }
    return this.props.children;
  }
}

/** A HOC that wraps Component with GracefullErrorBoundary */
export function withGracefulErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps: Omit<Props, "children">,
): React.ComponentType<P> {
  const WithGracefulErrorBoundary = (props: P) => (
    <GracefulErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </GracefulErrorBoundary>
  );
  return WithGracefulErrorBoundary;
}

export default GracefulErrorBoundary;
